/**
 * Single source of truth for "what view is the user on?".
 *
 * Used by both the bootstrap store (which derives the view type from
 * `window.location` before React mounts) and `usePageInfo` (which has
 * react-router params available). Both call `deriveViewType` so the
 * decision logic stays in one place.
 *
 * View types:
 *  - 'context'           — a normal /:user/:graphName graph page
 *  - 'synthesis'         — Top Graphs synthesis. Logged-in form:
 *                          /:user/edit?showcontexts=1. Public form:
 *                          /:user/?showcontexts=1 (no /edit).
 *  - 'conceptsSynthesis' — Top Concepts synthesis. Logged-in forms:
 *                          /:user/edit (default) or /:self (shortcut).
 *                          Public form: /:user/?showconcepts=1.
 *  - 'search'            — /:user/find
 */

export const VIEW_TYPES = {
    CONTEXT: 'context',
    SYNTHESIS: 'synthesis',
    CONCEPTS_SYNTHESIS: 'conceptsSynthesis',
    SEARCH: 'search',
}

/**
 * Derive the view type from URL parts.
 *
 * @param {Object} params
 * @param {string|null} params.mainPage - First path segment (typically the username).
 * @param {string|null} params.secondPage - Second path segment ('edit' / 'find' / context name).
 * @param {boolean} params.isViewingGraph - True when the path has a third segment
 *     (e.g. /:user/:context/:editor — this disambiguates /:user/edit (top
 *     synthesis) from /:user/:context/edit (a real graph)).
 * @param {URLSearchParams|null} params.searchParams - The URL's query params.
 * @param {string|null} [params.loggedInUsername] - The CURRENTLY LOGGED-IN
 *     user's username. Only used to recognise the /:self shorthand
 *     (mainPage === loggedInUsername) as top synthesis for the owner.
 *     MUST NOT be the URL owner's username — anonymous visitors to
 *     /:someone-elses-graph should stay on the default 'context' branch.
 *     Pass null (not an empty string) when there is no logged-in user.
 * @returns {'context'|'synthesis'|'conceptsSynthesis'|'search'}
 */
export function deriveViewType({
    mainPage,
    secondPage,
    isViewingGraph,
    searchParams,
    loggedInUsername = null,
}) {
    if (secondPage === 'find' && !isViewingGraph) {
        return VIEW_TYPES.SEARCH
    }

    // Normalise falsy values (null, undefined, empty string) to null so
    // callers that pull the username from React context (which defaults
    // to '') don't accidentally match `'' === ''` for an empty mainPage.
    const selfUsername = loggedInUsername || null

    // Public synthesis URLs have no /edit segment — routes.js generates
    // `/:username/?showcontexts=1` and `/:username/?showconcepts=1` via
    // `showAllUrlPublic` / `showAllConceptsUrlPublic`. Recognize that shape
    // via the query param alone. Must come before the "must be
    // logged-in-as-self" branch so public visitors aren't misclassified.
    const hasSynthesisQueryParam =
        !!searchParams &&
        (searchParams.get('showcontexts') === '1' ||
            searchParams.get('showconcepts') === '1')

    const viewingTopSynthesis =
        (secondPage === 'edit' && !isViewingGraph) ||
        (!secondPage && selfUsername !== null && selfUsername === mainPage) ||
        (!secondPage && !isViewingGraph && hasSynthesisQueryParam)

    if (viewingTopSynthesis) {
        return searchParams && searchParams.get('showcontexts') === '1'
            ? VIEW_TYPES.SYNTHESIS
            : VIEW_TYPES.CONCEPTS_SYNTHESIS
    }

    return VIEW_TYPES.CONTEXT
}

/**
 * Convenience: derive view type from a `window.location`-like object.
 * Bootstrap uses this before react-router is mounted.
 *
 * @param {Location|{pathname:string, search:string}} location
 * @param {string|null} loggedInUsername - See `deriveViewType` — this must be
 *     the actually logged-in user (or null), NOT the URL owner.
 */
export function deriveViewTypeFromLocation(location, loggedInUsername = null) {
    if (!location) return VIEW_TYPES.CONTEXT
    const pathParts = (location.pathname || '')
        .split('/')
        .filter(Boolean)
    return deriveViewType({
        mainPage: pathParts[0] || null,
        secondPage: pathParts[1] || null,
        isViewingGraph: pathParts.length > 2,
        searchParams: new URLSearchParams(location.search || ''),
        loggedInUsername,
    })
}
